import { useEffect, useState } from 'react'
import { MINUTOS_PARA_EDITAR, semAssinatura, type MensagemDto } from '@crm/shared'
import { ErroRequisicao } from '../../api/cliente'
import { useEditarMensagem } from '../../api/hooks-chat'
import { Aviso, Botao, Modal } from '../../componentes/ui'

/**
 * Editar uma mensagem já enviada (ADR 0026).
 *
 * O campo vem sem a assinatura: ela é posta de novo pelo servidor, do mesmo
 * jeito que no envio. Deixar a pessoa mexer nela abriria a porta para uma
 * mensagem editada sair sem nome, ou com o nome de outro.
 */
export function EditarMensagem({
  chamadoId,
  mensagem,
  aoFechar,
}: {
  chamadoId: string
  mensagem: MensagemDto | null
  aoFechar: () => void
}) {
  const editar = useEditarMensagem()
  const [texto, setTexto] = useState('')

  useEffect(() => {
    if (!mensagem) return
    setTexto(semAssinatura(mensagem.conteudo ?? ''))
    editar.reset()
  }, [mensagem?.id])

  if (!mensagem) return null

  const original = semAssinatura(mensagem.conteudo ?? '')
  const minutosPassados = (Date.now() - new Date(mensagem.enviadaEm).getTime()) / 60_000
  const restam = Math.max(0, Math.ceil(MINUTOS_PARA_EDITAR - minutosPassados))
  const erro = editar.error instanceof ErroRequisicao ? editar.error.corpo.mensagem : null
  const podeSalvar = texto.trim().length > 0 && texto.trim() !== original.trim()

  function salvar() {
    if (!mensagem || !podeSalvar) return
    editar.mutate({ chamadoId, mensagemId: mensagem.id, conteudo: texto }, { onSuccess: aoFechar })
  }

  return (
    <Modal
      aberto
      titulo="Editar mensagem"
      aoFechar={aoFechar}
      // Texto digitado: um clique fora sem querer jogaria a correção fora.
      fecharAoClicarFora={false}
      rodape={
        <>
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao
            variante="primario"
            carregando={editar.isPending}
            disabled={!podeSalvar}
            onClick={salvar}
          >
            Salvar
          </Botao>
        </>
      }
    >
      <div className="space-y-3">
        <textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            // Como na caixa da conversa: Enter salva, Shift+Enter quebra a linha.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              salvar()
            }
          }}
          rows={5}
          autoFocus
          className="w-full resize-none rounded-md border border-tinta-200 px-3 py-2 text-sm outline-none focus:border-marca focus:ring-2 focus:ring-marca/20"
        />
        <p className="text-xs text-tinta-500">
          O cliente vê o texto novo com a marca "Editada", e a sua assinatura continua em cima. Aqui
          no CRM o texto anterior fica guardado.{' '}
          {restam > 0
            ? `Dá para editar por mais ${restam} ${restam === 1 ? 'minuto' : 'minutos'}.`
            : 'O prazo de edição do WhatsApp acabou.'}
        </p>
        {erro ? <Aviso tipo="erro">{erro}</Aviso> : null}
      </div>
    </Modal>
  )
}
